import theme from '@/style';
import {cn, firstUppercase, toPriceStr} from '@/utils';
import {View} from 'react-native';
import Text from '@basicComponents/text';
import React, {createContext, useContext, useMemo} from 'react';
import {Input} from '@rneui/themed';
import {NativeTouchableOpacity} from '@/components/basic/touchable-opacity';
import {useInnerStyle} from './recharge.hooks';
import {BalanceListItem, PayMethod} from './recharge.service';
import BoxShadow from '@/components/basic/shadow';
import LinearGradient from '@/components/basic/linear-gradient';
import {Trans, useTranslation} from 'react-i18next';
import RechargeTip from './recharge-tip';
// import {getGiveSentStrs} from './recharge.utils';
import {getTotalSentPriceStrWithTabBonus} from './util/recharge-tab-bonus.utils';
import {useRechargeStore} from './recharge.store';
import {VipWeekCardConfigVO} from '../vip/vip.service';
import {canUseType} from '../vip/week-card';
export const WeekCardRechargeTabContext = createContext<{
  fromWeekCard: boolean;
  weekCardList: VipWeekCardConfigVO[];
  activeCardId: number;
  updateActiveCardId: (cardId: number) => void;
}>({
  fromWeekCard: false,
  weekCardList: [],
  activeCardId: 0,
  updateActiveCardId: () => {},
});

export interface RechargeSelectProps {
  min: number;
  max: number;
  balance: string;
  payMethod?: PayMethod;
  extraBounsBalanceIndex?: number;
  onChangeBalance: (balance: string) => void;
  balanceList?: BalanceListItem[];
}

const RechargeSelect: React.FC<RechargeSelectProps> = ({
  balance,
  onChangeBalance,
  balanceList,
  min,
  max,
  payMethod,
  extraBounsBalanceIndex,
}) => {
  const {i18n} = useTranslation();
  const {
    inputStyle,
    compressedInputStyle,
    inputStyles,
    selectStyles,
    size: {calcActualSize},
  } = useInnerStyle();
  const {fromWeekCard, activeCardId, updateActiveCardId, weekCardList} =
    useContext(WeekCardRechargeTabContext);

  // 获取当前选中的recharge tab
  const {activeTabId, tabs} = useRechargeStore();
  const activeTab = tabs.find(tab => tab.id === activeTabId);

  const giveSentStr = useMemo(() => {
    if (fromWeekCard) {
      return '';
    }
    if (extraBounsBalanceIndex == null || balanceList == null) {
      return '';
    }
    return '';
    // return getGiveSentStrs(payMethod, balanceList[extraBounsBalanceIndex]);
  }, [balanceList, extraBounsBalanceIndex, fromWeekCard]);

  return (
    <View
      style={[
        theme.flex.col,
        theme.borderRadius.m,
        theme.padding.l,
        theme.background.white,
      ]}>
      <View style={[theme.flex.col, theme.margin.btms]}>
        {!!fromWeekCard && (
          <span className="text-xs text-t2 mb-2">
            <span className="text-[#F15802] mr-1">*</span>
            {i18n.t('vip.rules.buyTip')}
          </span>
        )}
        <View
          style={[
            theme.flex.col,
            theme.background.lightGrey,
            selectStyles.inputWrap,
            theme.borderRadius.xs,
            theme.margin.btms,
            theme.position.rel,
          ]}>
          <Input
            containerStyle={[
              theme.padding.lrm,
              !giveSentStr
                ? inputStyles.container
                : inputStyles.compressedContainer,
            ]}
            inputContainerStyle={inputStyles.inputContainer}
            style={!giveSentStr ? inputStyle : compressedInputStyle}
            errorStyle={inputStyles.error}
            disabled={fromWeekCard}
            keyboardType="numeric"
            inputMode="numeric"
            value={balance}
            onChangeText={value => {
              if (value && !value.startsWith('0')) {
                if (/^[0-9]+$/.test(value)) {
                  if (max > 0) {
                    if (Number(value) <= max) {
                      onChangeBalance(value);
                    }
                  } else {
                    if (Number(value) <= 50000) {
                      onChangeBalance(value);
                    }
                  }
                } else {
                  onChangeBalance(balance || '');
                }
              } else {
                onChangeBalance('');
              }
            }}
            placeholder={i18n.t('recharge-page.label.enter')}
          />
          {!!giveSentStr && (
            <Text
              style={[
                theme.position.abs,
                {
                  bottom: calcActualSize(8),
                  left: calcActualSize(12),
                },
              ]}
              calc
              color={theme.backgroundColor.orange}
              fontSize={10}
              fontWeight="700">
              {i18n.t('recharge.tip.extra', {bonus: giveSentStr})}
            </Text>
          )}
        </View>
        <View style={[theme.margin.btmxxs]}>
          <Text calc accent>
            <Trans
              i18nKey="recharge-page.label.enterText"
              components={{
                mark: <Text calc primary />,
              }}>
              {i18n.t('recharge-page.label.enterText')}
            </Trans>
          </Text>
        </View>
        <View style={[theme.flex.row]}>
          <Text accent fontSize={theme.fontSize.m}>
            {i18n.t('recharge-page.label.min')}
          </Text>
          <Text
            style={[theme.margin.leftxxs]}
            second
            blod
            fontSize={theme.fontSize.m}>
            {min ? toPriceStr(min, {fixed: 0, thousands: true}) : '--'}
          </Text>
          <Text
            style={[theme.margin.leftxxl]}
            accent
            fontSize={theme.fontSize.m}>
            {i18n.t('recharge-page.label.max')}
          </Text>
          <Text
            style={[theme.margin.leftxxs]}
            second
            blod
            fontSize={theme.fontSize.m}>
            {max ? toPriceStr(max, {fixed: 0, thousands: true}) : '--'}
          </Text>
        </View>
      </View>
      {fromWeekCard ? (
        <div className="grid grid-cols-3 gap-2">
          {weekCardList.map(card => {
            const cardType = canUseType.find(type =>
              card.cardName?.toLowerCase().includes(type),
            );
            if (!cardType) {
              return null;
            }
            return (
              <div
                onClick={() => updateActiveCardId(card.cardId || 0)}
                className={cn(
                  'rounded h-10 flex items-center justify-center font-bold',
                  activeCardId === card.cardId
                    ? 'bg-c1 text-white'
                    : 'bg-gradient-to-b from-[#F2F4F9] to-[#F3F5FB] shadow-[0_2px_0_0_#C3C8DC] text-black',
                )}>
                {`${firstUppercase(cardType)} Card`}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-4 pt-2 gap-2">
          {balanceList?.map((bl, index) => (
            <NativeTouchableOpacity
              key={index}
              style={[selectStyles.item, theme.flex.col, theme.position.rel]}
              onPress={() => onChangeBalance(bl.balance + '')}>
              <RechargeTip>
                {`${getTotalSentPriceStrWithTabBonus(
                  (bl.balance || 0) + '',
                  payMethod,
                  bl,
                  activeTab,
                  true,
                )}`}
              </RechargeTip>
              {bl.balance + '' !== balance ? (
                <BoxShadow
                  shadowStyle={{
                    radius: theme.borderRadiusSize.xs,
                    out: {x: 0, y: 2, blur: 0, color: '#C3C8DC'},
                  }}>
                  <LinearGradient
                    start={{x: 0, y: 0}}
                    end={{x: 0, y: 1}}
                    colors={['#F2F4F9', '#F3F5FB']}
                    style={[
                      theme.flex.center,
                      selectStyles.item,
                      theme.background.primary,
                      theme.borderRadius.xs,
                    ]}>
                    <Text
                      color={theme.basicColor.dark}
                      blod
                      fontSize={theme.fontSize.m}>
                      {toPriceStr(bl.balance, {
                        fixed: 0,
                        showCurrency: false,
                        thousands: true,
                      })}
                    </Text>
                  </LinearGradient>
                </BoxShadow>
              ) : (
                <View
                  style={[
                    theme.flex.center,
                    selectStyles.item,
                    theme.background.primary,
                    theme.borderRadius.xs,
                  ]}>
                  <Text
                    color={theme.basicColor.white}
                    blod
                    fontSize={theme.fontSize.m}>
                    {toPriceStr(bl.balance, {
                      fixed: 0,
                      showCurrency: false,
                      thousands: true,
                    })}
                  </Text>
                </View>
              )}
            </NativeTouchableOpacity>
          ))}
        </div>
      )}
    </View>
  );
};

export default RechargeSelect;
